import { useState, useEffect } from 'react';
import { useApp } from '../../context';
import api from '../../hooks/useApi';
import { formatDate } from '../../utils/format';

// Read-only, money-free view of a SOW that ANY role can open (e.g. from the
// system-wide search). Shows what the project is, who owns it, its status and
// its milestones with their status — but no financials. Full detail (values,
// invoicing, resources) stays on SOWDetailPage for roles with tracker access.

const SOW_STATUS_CLS = {
  Active: 'bg-green-100 text-green-700', Issued: 'bg-blue-100 text-blue-700',
  Draft: 'bg-gray-100 text-gray-500', Completed: 'bg-purple-100 text-purple-600',
  Complete: 'bg-purple-100 text-purple-600', Cancelled: 'bg-red-100 text-red-400',
  'On Hold': 'bg-amber-100 text-amber-700', Expired: 'bg-amber-100 text-amber-700',
  'Awaiting Payment': 'bg-teal-100 text-teal-700',
};
const MS_STATUS_CLS = {
  Awaiting: 'bg-gray-100 text-gray-600', 'Ready to bill': 'bg-amber-100 text-amber-700',
  Invoiced: 'bg-blue-100 text-blue-700', Paid: 'bg-emerald-100 text-emerald-700',
  Cancelled: 'bg-red-100 text-red-400',
};
const HEALTH_LABELS = { on_track: 'On Track', at_risk: 'At Risk', delayed: 'Delayed', blocked: 'Blocked', on_hold: 'On Hold', uat: 'UAT', complete: 'Complete' };
const HEALTH_CLS = { on_track: 'bg-emerald-100 text-emerald-700', at_risk: 'bg-amber-100 text-amber-700', delayed: 'bg-red-100 text-red-700', blocked: 'bg-red-100 text-red-700', on_hold: 'bg-gray-100 text-gray-600', uat: 'bg-blue-100 text-blue-700', complete: 'bg-teal-100 text-teal-700' };

export default function SowOverviewPage() {
  const { detailId, navigate } = useApp();
  const [sow, setSow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!detailId) return;
    setLoading(true); setError('');
    api.get(`sows/${detailId}/overview`)
      .then(setSow)
      .catch(() => setError('This project could not be loaded.'))
      .finally(() => setLoading(false));
  }, [detailId]);

  if (loading) return <div className="text-center py-8 text-[10px] text-gray-400">Loading…</div>;
  if (error || !sow) return <div className="text-center py-8 text-[10px] text-gray-400">{error || 'Not found.'}</div>;

  const milestones = sow.milestones || [];
  const done = milestones.filter(m => ['Invoiced', 'Paid', 'Complete', 'Completed'].includes(m.status)).length;

  return (
    <div className="fade-in">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-lg font-extrabold text-navy font-mono">{sow.reference}</span>
        {sow.status && <span className={`text-[9px] px-2 py-0.5 rounded font-semibold ${SOW_STATUS_CLS[sow.status] || 'bg-gray-100 text-gray-500'}`}>{sow.status}</span>}
      </div>
      <div className="text-sm text-gray-700 mb-1">{sow.detail}</div>
      <div className="text-[10px] text-gray-400 mb-4">Read-only overview. Financial detail is not shown here.</div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
        {[
          ['Client', sow.client_name],
          ['Owner', sow.owner_name],
          ['Type', sow.type],
          ['Forecast completion', sow.forecast_completion ? formatDate(sow.forecast_completion) : null],
        ].map(([label, val]) => (
          <div key={label} className="bg-white border border-gray-200 rounded p-2">
            <div className="text-[7px] text-gray-400 uppercase font-bold">{label}</div>
            <div className="text-[11px] font-semibold text-gray-700">{val || '—'}</div>
          </div>
        ))}
      </div>

      {sow.health && (
        <div className="bg-white border border-gray-200 rounded-lg p-3 mb-4">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[8px] font-bold uppercase text-gray-400">Latest health</span>
            {sow.health.delivery_status && (
              <span className={`text-[8px] px-1.5 py-0.5 rounded font-bold ${HEALTH_CLS[sow.health.delivery_status] || 'bg-gray-100 text-gray-600'}`}>
                {HEALTH_LABELS[sow.health.delivery_status] || sow.health.delivery_status}
              </span>
            )}
            {sow.health.week_commencing && <span className="text-[8px] text-gray-400">w/c {formatDate(sow.health.week_commencing)}</span>}
          </div>
          {sow.health.recent_progress && <div className="text-[10px] text-gray-700 leading-relaxed max-w-3xl"><span className="font-semibold text-gray-500">Recent: </span>{sow.health.recent_progress}</div>}
          {sow.health.next_period && <div className="text-[10px] text-gray-700 leading-relaxed max-w-3xl mt-0.5"><span className="font-semibold text-gray-500">Next: </span>{sow.health.next_period}</div>}
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
          <span className="text-[10px] font-bold text-navy uppercase">Milestones</span>
          <span className="text-[9px] text-gray-400">{done}/{milestones.length} complete</span>
        </div>
        {milestones.length === 0 ? (
          <div className="px-3 py-4 text-[10px] text-gray-400 text-center">No milestones.</div>
        ) : (
          <table className="w-full">
            <thead><tr>
              {['Milestone', 'Description', 'Contract', 'Forecast', 'Status'].map(h => (
                <th key={h} className="px-3 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {milestones.map(m => (
                <tr key={m.id} className="border-b border-gray-50 last:border-0">
                  <td className="px-3 py-1.5 text-[10px] font-semibold font-mono">{m.milestone_code}</td>
                  <td className="px-3 py-1.5 text-[10px] text-gray-700">{m.description}</td>
                  <td className="px-3 py-1.5 text-[9px] text-gray-500">{formatDate(m.contract_date) || '—'}</td>
                  <td className="px-3 py-1.5 text-[9px] text-gray-500">{formatDate(m.forecast_date) || '—'}</td>
                  <td className="px-3 py-1.5"><span className={`text-[8px] px-1.5 py-0.5 rounded font-semibold ${MS_STATUS_CLS[m.status] || 'bg-gray-100 text-gray-500'}`}>{m.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
